import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight, Calendar, Check, ChevronLeft, ChevronRight, ImageOff, X, ZoomIn } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { RevealText, Reveal } from "@/components/Reveal";

const EASE = [0.22, 1, 0.36, 1];

// Editorial carousel — click arrows to move through 5-8 images per location
const LocationCarousel = ({ images, name, idx }) => {
  const validImages = (images || []).filter(Boolean);
  const hasImages = validImages.length > 0;
  const [i, setI] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const touchStartX = useRef(null);
  const total = validImages.length;
  const prev = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setI((v) => (v - 1 + total) % total);
  };
  const next = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setI((v) => (v + 1) % total);
  };

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxOpen(false);
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasImages) {
    return (
      <div
        data-testid={`location-carousel-${idx}`}
        className="relative aspect-[16/10] rounded-tl-[4rem] overflow-hidden bg-sagelight/40 flex flex-col items-center justify-center text-center px-8"
      >
        <ImageOff size={32} className="text-sagedark/60 mb-4" strokeWidth={1.4} />
        <p className="overline text-sagedark tracking-[0.25em]">Photos coming soon</p>
        <p className="mt-2 font-serif text-lg md:text-xl text-ink/80">{name}</p>
      </div>
    );
  }

  return (
    <>
      <div
        data-testid={`location-carousel-${idx}`}
        className="relative aspect-[16/10] rounded-tl-[4rem] overflow-hidden bg-stone2 group"
      >
        <AnimatePresence mode="wait">
          <motion.button
            key={i}
            type="button"
            onClick={() => setLightboxOpen(true)}
            data-testid={`location-image-open-${idx}`}
            aria-label={`Open ${name} image ${i + 1} in viewer`}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.55, ease: EASE }}
            className="absolute inset-0 h-full w-full flex items-center justify-center cursor-zoom-in"
          >
            <img
              src={validImages[i]}
              alt={`${name} — ${i + 1} of ${total}`}
              className="max-h-full max-w-full w-auto h-auto object-contain"
              loading="lazy"
              draggable={false}
            />
            <span className="absolute bottom-4 right-4 h-10 w-10 rounded-full bg-cream/85 backdrop-blur border border-ink/10 text-ink flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <ZoomIn size={18} strokeWidth={1.6} />
            </span>
          </motion.button>
        </AnimatePresence>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            data-testid={`location-prev-${idx}`}
            aria-label="Previous image"
            className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-cream/85 hover:bg-cream text-ink backdrop-blur border border-ink/10 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
          >
            <ChevronLeft size={20} strokeWidth={1.6} />
          </button>
          <button
            type="button"
            onClick={next}
            data-testid={`location-next-${idx}`}
            aria-label="Next image"
            className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-cream/85 hover:bg-cream text-ink backdrop-blur border border-ink/10 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
          >
            <ChevronRight size={20} strokeWidth={1.6} />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {validImages.map((_, k) => (
              <button
                key={k}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setI(k);
                }}
                data-testid={`location-dot-${idx}-${k}`}
                aria-label={`Show image ${k + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  k === i ? "w-6 bg-cream" : "w-1.5 bg-cream/60 hover:bg-cream/80"
                }`}
              />
            ))}
          </div>
          <span className="absolute top-4 right-4 rounded-full bg-ink/60 backdrop-blur px-3 py-1 text-[11px] font-medium text-cream tracking-wider">
            {i + 1} / {total}
          </span>
        </>
      )}
      </div>

      {/* Fullscreen lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            key="loc-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-ink/95 flex items-center justify-center"
            data-testid={`location-lightbox-${idx}`}
            onClick={() => setLightboxOpen(false)}
            onTouchStart={(e) => { touchStartX.current = e.touches?.[0]?.clientX ?? null; }}
            onTouchEnd={(e) => {
              const endX = e.changedTouches?.[0]?.clientX;
              if (touchStartX.current === null || endX == null) return;
              const dx = endX - touchStartX.current;
              if (dx > 50) prev();
              else if (dx < -50) next();
              touchStartX.current = null;
            }}
          >
            <button
              type="button"
              aria-label="Close"
              data-testid={`location-lightbox-close-${idx}`}
              onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
              className="absolute top-5 right-5 md:top-8 md:right-8 h-11 w-11 rounded-full bg-cream/10 hover:bg-cream/20 text-cream flex items-center justify-center transition-colors"
            >
              <X size={22} />
            </button>
            {total > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous image"
                  onClick={(e) => { e.stopPropagation(); prev(); }}
                  className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-cream/10 hover:bg-cream/20 text-cream flex items-center justify-center transition-colors"
                >
                  <ChevronLeft size={26} />
                </button>
                <button
                  type="button"
                  aria-label="Next image"
                  onClick={(e) => { e.stopPropagation(); next(); }}
                  className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-cream/10 hover:bg-cream/20 text-cream flex items-center justify-center transition-colors"
                >
                  <ChevronRight size={26} />
                </button>
              </>
            )}
            <motion.img
              key={validImages[i]}
              src={validImages[i]}
              alt={`${name} — ${i + 1} of ${total}`}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: EASE }}
              className="max-h-[88vh] max-w-[92vw] object-contain select-none"
              draggable={false}
            />
            <p className="absolute bottom-5 left-1/2 -translate-x-1/2 text-cream/70 text-xs tracking-[0.2em] uppercase">
              {i + 1} / {total} · {name}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const LOCATIONS = [
  {
    name: "Dr. Reaux Property",
    city: "New Iberia, LA",
    fee: "$40 fee",
    appointment: true,
    desc: "A photographer's dream — open fields, rustic buildings, lush greenery, a serene pond, breathtaking sunsets, and a picturesque chapel. Seasonal wildflowers add extra beauty. You can return here session after session and still discover new corners to capture.",
    img: "/dr-reaux-1.jpg",
    images: [
      "/dr-reaux-1.jpg",
      "/dr-reaux-2.jpg",
      "/dr-reaux-3.jpg",
      "/dr-reaux-4.jpg",
      "/dr-reaux-5.jpg",
      "/dr-reaux-6.jpg",
      "/dr-reaux-7.jpg",
      "/dr-reaux-8.jpg",
      "/dr-reaux-9.jpg",
      "/dr-reaux-10.jpg",
      "/dr-reaux-11.jpg",
      "/dr-reaux-12.jpg",
      "/dr-reaux-13.jpg",
    ],
  },
  {
    name: "UL Alumni Center",
    city: "Lafayette, LA",
    fee: "$25 fee",
    appointment: true,
    desc: "Nestled on the University of Louisiana at Lafayette campus, surrounded by magnificent oak trees and classic Louisiana architecture. The grand iron gates and quaint little bridge are client favorites for timeless, elegant portraits.",
    img: "/ul-alumni-1.webp",
    images: [
      "/ul-alumni-1.webp",
      "/ul-alumni-2.webp",
      "/ul-alumni-3.webp",
      "/ul-alumni-4.webp",
      "/ul-alumni-5.webp",
      "/ul-alumni-6.webp",
      "/ul-alumni-7.webp",
      "/ul-alumni-8.webp",
      "/ul-alumni-9.webp",
      "/ul-alumni-10.webp",
      "/ul-alumni-11.jpg",
      "/ul-alumni-12.jpg",
      "/ul-alumni-13.jpg",
      "/ul-alumni-14.jpg",
      "/ul-alumni-15.jpg",
    ],
  },
  {
    name: "Southside Park",
    city: "Lafayette, LA",
    fee: "Free",
    appointment: false,
    desc: "A scenic outdoor location along the Vermilion River. Features a picturesque dock for waterfront reflections, open fields, towering oak trees, and winding walking trails — perfect for that golden-hour glow filtering through the trees.",
    img: "/southside-1.jpg",
    images: [
      "/southside-1.jpg",
      "/southside-2.jpg",
      "/southside-3.jpg",
      "/southside-4.jpg",
      "/southside-5.jpg",
      "/helms-4.jpg",
      "/helms-13.jpg",
      "/helms-22.jpg",
      "/helms-58.jpg",
      "/helms-94.jpg",
    ],
  },
  {
    name: "Magnolia Greenhouse",
    city: "Youngsville, LA",
    fee: "$50 fee",
    appointment: true,
    desc: "A dreamy, light-filled glass-paneled greenhouse flooded with natural light. Lush greenery inside and around the space creates an ethereal, airy atmosphere for timeless and elegant portraits.",
    img: "/magnolia-1.webp",
    images: [
      "/magnolia-1.webp",
      "/magnolia-2.webp",
      "/magnolia-3.webp",
      "/magnolia-4.webp",
      "/magnolia-5.webp",
      "/champagne-16.jpg",
      "/champagne-28.jpg",
      "/perkins-20.jpg",
      "/perkins-28.jpg",
    ],
  },
  {
    name: "St. Julien Park",
    city: "Broussard, LA",
    fee: "Free",
    appointment: false,
    desc: "A vibrant, family-friendly park with picturesque walking trails, open green spaces, charming wooden bridges, and a scenic pond — a tranquil waterfront element for serene reflections and breathtaking golden-hour shots.",
    img: "/st-julien-1.jpg",
    images: [
      "/st-julien-1.jpg",
      "/st-julien-2.jpg",
      "/st-julien-3.jpg",
      "/elizabeth-5.jpg",
      "/elizabeth-31.jpg",
      "/elizabeth-47.jpg",
      "/elizabeth-67.jpg",
      "/elizabeth-75.jpg",
      "/st-julien-4.jpg",
      "/st-julien-5.jpg",
    ],
  },
  {
    name: "Lake Martin",
    city: "Breaux Bridge, LA",
    fee: "Free",
    appointment: false,
    desc: "A breathtaking natural gem — cypress trees draped in Spanish moss, reflective waters, and diverse wildlife. Wooden boardwalks and tranquil waterfront areas make it perfect for engagements, maternity, and adventurous senior portraits.",
    img: "/lake-martin-1.jpg",
    images: [
      "/lake-martin-1.jpg",
      "/lake-martin-2.jpg",
      "/lake-martin-3.jpg",
      "/hanks-1.jpg",
      "/hanks-7.jpg",
      "/hanks-25.jpg",
      "/hanks-46.jpg",
      "/hanks-50.jpg",
    ],
  },
  {
    name: "Jungle Gardens",
    city: "New Iberia, LA",
    fee: "$75 fee",
    appointment: false,
    desc: "A breathtaking 170-acre botanical paradise — way more than pictures can show. Lush greenery, winding pathways, and majestic oak trees draped in Spanish moss offer endless opportunities for capturing natural beauty.",
    img: "/senior-13.jpg",
    images: [
      "/senior-13.jpg",
      "/senior-14.jpg",
      "/senior-15.jpg",
      "/senior-16.jpg",
      "/senior-17.jpg",
      "/boudreaux-39.jpg",
      "/boudreaux-31.jpg",
      "/boudreaux-3.jpg",
      "/maternity-jungle.webp",
      "/couples-2.webp",
      "/couples-3.webp",
      "/couples-4.webp",
      "/couples-5.webp",
    ],
  },
  {
    name: "BeauBassin Farm & Gardens",
    city: "Sunset, LA",
    fee: "$50 / half hour · $100 / hr",
    appointment: true,
    desc: "A storybook working farm with a rustic wooden barn, grand oak canopies, a tranquil fountain, and brick pathways winding through the gardens. Golden morning light and layered greenery make it unforgettable for family, maternity, and seasonal mini sessions.",
    img: "/west-31.webp",
    images: [
      "/west-31.webp",
      "/aranza-11.webp",
      "/jguidry-5.webp",
      "/aranza-55.webp",
      "/andrade-39.webp",
      "/broussard-35.webp",
      "/guidry-25.webp",
    ],
  },
  {
    name: "My Neighborhood",
    city: "Lafayette, LA",
    fee: "Free",
    appointment: false,
    desc: "A peaceful, scenic common area with lush greenery, a tree-lined path, and a serene pond. Reflections on the water and golden-hour light make it perfect for effortless, timeless moments.",
    img: "/neighborhood-1.webp",
    images: [
      "/neighborhood-1.webp",
      "/neighborhood-2.webp",
      "/neighborhood-3.webp",
      "/neighborhood-4.webp",
      "/neighborhood-5.webp",
      "/neighborhood-6.webp",
      "/neighborhood-7.webp",
      "/neighborhood-8.webp",
      "/neighborhood-9.webp",
    ],
  },
  {
    name: "Downtown Parking Garage Rooftop",
    city: "Lafayette, LA",
    fee: "Free",
    appointment: false,
    desc: "A modern, urban rooftop with expansive city views and a sleek, minimalist vibe. Industrial textures and golden-hour lighting create a dramatic, city-chic backdrop with the Lafayette skyline.",
    img: "/parking-1.jpg",
    images: [
      "/parking-1.jpg",
      "/parking-2.jpg",
      "/parking-3.jpg",
      "/parking-4.jpg",
      "/parking-5.jpg",
      "/parking-6.jpg",
      "/parking-7.jpg",
      "/parking-8.jpg",
      "/parking-9.jpg",
      "/parking-10.jpg",
      "/parking-11.jpg",
      "/parking-12.jpg",
      "/eon-1.webp",
      "/eon-5.webp",
      "/eon-3.webp",
      "/eon-4.webp",
      "/eon-2.webp",
      "/eon-6.webp",
      "/eon-7.webp",
      "/eon-8.webp",
      "/eon-9.webp",
      "/eon-10.webp",
      "/eon-11.webp",
      "/eon-12.webp",
    ],
  },
  {
    name: "Rip Van Winkle Gardens",
    city: "New Iberia, LA",
    fee: "$50/hr · $100/hr bridal",
    appointment: true,
    desc: "A historic estate filled with lush tropical landscaping, vibrant flowers, and centuries-old oak trees draped in Spanish moss. The Joseph Jefferson Mansion adds romance and Southern charm — dreamy, storybook-worthy scenes for bridal, engagements, senior, maternity, and family. Standard sessions are $50/hour per client; bridal sessions are $100/hour.",
    img: "/lana-1.jpg",
    images: [
      "/lana-1.jpg",
      "/lana-2.jpg",
      "/lana-3.jpg",
      "/lana-4.jpg",
      "/lana-5.jpg",
      "/lana-6.jpg",
      "/lana-7.jpg",
      "/lana-8.jpg",
      "/lana-9.jpg",
      "/verret-1.webp",
      "/verret-2.webp",
      "/verret-3.webp",
      "/verret-4.webp",
      "/verret-5.webp",
      "/verret-6.webp",
      "/verret-7.webp",
    ],
  },
];

const BRIDAL_LOCATIONS = [
  "John Nickerson House",
  "Cypress Grove",
  "Afton Villa Gardens",
  "Arlington Mansion",
  "The Secret Garden",
  "Mouton Plantation",
  "Vermillionville",
  "Acadian Village",
  "Jungle Gardens",
  "Rip Van Winkle",
  "Dr. Reaux",
];

export default function Locations() {
  return (
    <div className="pt-8 md:pt-12">
      {/* HERO */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10">
        <p className="overline text-sage mb-6 flex items-center gap-2">
          <MapPin size={14} /> Locations
        </p>
        <RevealText
          as="h1"
          className="font-serif font-light text-ink leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl"
          lines={["Where we", "can create"]}
        />
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-2xl text-inksoft leading-relaxed">
            From cypress swamps to sunlit greenhouses and downtown rooftops — a curated list
            of favorite spots around Acadiana where I love to shoot. Some are free; others
            require a small permit fee. Reach out if you have a place close to your heart —
            we can plan a session there, too.
          </p>
        </Reveal>
      </section>

      {/* LOCATIONS LIST */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
        <div className="space-y-20 md:space-y-32">
          {LOCATIONS.map((l, i) => (
            <div
              key={l.name}
              data-testid={`location-${i}`}
              className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-center ${
                i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
              }`}
            >
              <Reveal className="md:col-span-7" y={60}>
                <LocationCarousel
                  images={l.images && l.images.length > 0 ? l.images : [l.img]}
                  name={l.name}
                  idx={i}
                />
              </Reveal>
              <Reveal className="md:col-span-5" delay={0.1}>
                <span className="font-serif text-sage/50 text-5xl md:text-6xl leading-none">
                  0{i + 1 < 10 ? i + 1 : i + 1}
                </span>
                <h2 className="mt-3 font-serif font-light text-ink text-3xl md:text-4xl lg:text-5xl leading-tight">
                  {l.name}
                </h2>
                <p className="mt-2 overline text-inkmuted flex items-center gap-2">
                  <MapPin size={12} /> {l.city}
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] uppercase tracking-[0.2em] ${
                      l.fee.toLowerCase() === "free"
                        ? "bg-sagelight text-sagedark"
                        : "bg-clay/40 text-ink"
                    }`}
                  >
                    <Check size={12} /> {l.fee}
                  </span>
                  {l.appointment && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 px-3.5 py-1.5 text-[11px] uppercase tracking-[0.2em] text-inksoft">
                      <Calendar size={12} /> Appointment required
                    </span>
                  )}
                </div>
                <p className="mt-6 text-inksoft leading-relaxed">{l.desc}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* BRIDAL / PERSONAL LOCATIONS */}
      <section className="bg-stone2 py-20 md:py-28">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <Reveal>
            <p className="overline text-sage mb-5">And so much more</p>
            <h2 className="font-serif font-light text-ink text-4xl md:text-5xl leading-tight max-w-2xl">
              Countless incredible places waiting to be captured.
            </h2>
            <p className="mt-6 text-inksoft leading-relaxed max-w-2xl">
              Some I have yet to photograph, others I long to revisit. I also find joy in
              shooting personal and meaningful places — the front yard of your new home, your
              grandpa&rsquo;s old barn, or that special piece of land where you dream of
              raising your family. Let&rsquo;s explore and create something truly
              unforgettable.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="overline text-sagedark mt-12 mb-4">Bridal locations</p>
            <div className="flex flex-wrap gap-2.5">
              {BRIDAL_LOCATIONS.map((b) => (
                <span
                  key={b}
                  data-testid={`bridal-${b.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex items-center rounded-full border border-ink/15 bg-cream px-4 py-2 text-sm text-ink"
                >
                  {b}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-sagelight py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-serif font-light text-ink text-4xl md:text-6xl leading-tight max-w-xl">
            Have a spot in mind? Let&rsquo;s make it happen.
          </h2>
          <Link
            to="/contact"
            data-testid="locations-contact-link"
            className="group inline-flex items-center gap-3 rounded-full bg-ink text-cream px-9 py-4 text-sm font-medium hover:bg-sagedark transition-colors"
          >
            Get in touch
            <ArrowUpRight
              size={17}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Link>
        </div>
      </section>
    </div>
  );
}
